import { useEffect, useRef, useState } from "react";
import { useParams, Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiError, extractFieldErrors, formatCurrency, buildAssetUrl } from "@/lib/api";
import { CONNECT_ENABLED } from "@/lib/featureFlags";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  ArrowLeft, Download, Send, Loader2, Plus, Trash2, FileText, Save, AlertCircle, Link2,
  Lock, Edit3, Ban, RefreshCcw, Mail, RotateCw, MailX,
} from "lucide-react";
import {
  Sheet, SheetContent, SheetDescription, SheetFooter, SheetHeader, SheetTitle,
} from "@/components/ui/sheet";
import { PriceDisplay } from "@/lib/plan";
import StatusHistory from "@/components/StatusHistory";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const PAY_STATUS = [
  { v: "awaiting_payment", l: "Awaiting payment" },
  { v: "paid", l: "Paid" },
  { v: "overdue", l: "Overdue" },
  { v: "write_off", l: "Write-off" },
  { v: "unable_to_collect", l: "Unable to collect" },
];

export default function InvoiceDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  // Phase P.6 — `booking_changed` URL flag + `syncing` state kept as
  // no-ops so external links carrying `?booking_changed=1` still work
  // (they just no-op — auto-refresh already ran server-side).
  const bookingChanged = searchParams.get("booking_changed") === "1";
  void bookingChanged;
  const [syncing, setSyncing] = useState(false);
  void syncing; void setSyncing;
  const [inv, setInv] = useState(null);
  const [customer, setCustomer] = useState(null);
  const [items, setItems] = useState([]);
  const [savingItems, setSavingItems] = useState(false);
  const [sendOpen, setSendOpen] = useState(false);
  // Phase 62.5 — `?resend=<sendId>` pre-fills the dialog with the prior send's
  // body/subject/recipients so the manager doesn't retype anything.
  const resendId = searchParams.get("resend");
  const [prefillSendId, setPrefillSendId] = useState(null);
  const [allTemplates, setAllTemplates] = useState([]);
  const [switchingTpl, setSwitchingTpl] = useState("");
  // Phase O — email_sending feature flag from PLAN_LIMITS; disables Send button
  // for Basic/Trial tenants and surfaces a tooltip pointing at the upgrade.
  const [emailSendingGated, setEmailSendingGated] = useState(false);
  const [gatedPlanCode, setGatedPlanCode] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get(`/invoices/${id}`);
      setInv(data);
      setItems(data.line_items || []);
      const c = await api.get(`/customers/${data.customer_id}`).catch(() => null);
      setCustomer(c?.data || null);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    }
  };

  useEffect(() => { load(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    api.get("/invoice-templates").then(({ data }) => setAllTemplates(data || [])).catch(() => {});
  }, []);
  // Phase O — fetch plan limits so we know whether email_sending is enabled.
  useEffect(() => {
    api.get("/plan/limits").then(({ data }) => {
      const feats = data?.limits?.features || [];
      setEmailSendingGated(!feats.includes("email_sending"));
      setGatedPlanCode(data?.plan_code || "");
    }).catch(() => {});
  }, []);
  // Phase 62.5 — react to `?resend=<id>` arriving (from the Sent emails list).
  useEffect(() => {
    if (resendId) {
      setPrefillSendId(resendId);
      setSendOpen(true);
    }
  }, [resendId]);

  const [pendingPaid, setPendingPaid] = useState(false);
  const [paidMethods, setPaidMethods] = useState([]);
  const [paidMethodCode, setPaidMethodCode] = useState("");
  // Phase 37 — edit drawer + void dialog state.
  const [editOpen, setEditOpen] = useState(false);
  const [voidOpen, setVoidOpen] = useState(false);
  const [voidReason, setVoidReason] = useState("");
  const [voiding, setVoiding] = useState(false);
  // Phase 61.1 — manual draft → issued promotion.
  const [issuing, setIssuing] = useState(false);

  useEffect(() => {
    api.get("/settings/paid-methods").then(({ data }) => setPaidMethods(data || [])).catch(() => {});
  }, []);

  if (!inv) return <div className="page text-muted-foreground text-sm">Loading…</div>;

  const updateItem = (i, k, v) => {
    const next = [...items];
    next[i] = { ...next[i], [k]: v };
    if (k === "quantity" || k === "unit_price") {
      next[i].line_total = Number(((Number(next[i].quantity) || 0) * (Number(next[i].unit_price) || 0)).toFixed(2));
    }
    setItems(next);
  };
  const addItem = () => setItems([...items, { description: "", quantity: 1, unit_price: 0, line_total: 0 }]);
  const removeItem = (i) => setItems(items.filter((_, idx) => idx !== i));

  const saveItems = async () => {
    setSavingItems(true);
    try {
      const { data } = await api.put(`/invoices/${id}/line-items`, { line_items: items });
      setInv(data);
      setItems(data.line_items);
      toast.success("Line items saved");
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally { setSavingItems(false); }
  };

  const setPaymentStatus = async (v) => {
    if (v === "paid") {
      setPaidMethodCode("");
      setPendingPaid(true);
      return;
    }
    try {
      const { data } = await api.patch(`/invoices/${id}/payment-status`, { payment_status: v });
      setInv(data);
      toast.success("Status updated");
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };

  const confirmPaid = async () => {
    if (!paidMethodCode) { toast.error("Pick a paid method"); return; }
    try {
      const { data } = await api.patch(`/invoices/${id}/payment-status`, {
        payment_status: "paid",
        paid_method_code: paidMethodCode,
      });
      setInv(data);
      setPendingPaid(false);
      toast.success("Marked as paid");
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };

  // Phase 62 hotfix — always go through the dynamic `/pdf-stream` endpoint
  // instead of the static-mount `inv.pdf_url`. The static path 404s when
  // the on-disk PDF is missing (every Emergent deploy wipes the cache);
  // the stream endpoint always re-renders if the file isn't there.
  // Phase A.1 — append `pdf_cache_bust_token` (set whenever
  // rebuild_invoice_from_live_data runs) so the iframe re-fetches after a
  // rebuild instead of serving the previously cached response.
  const pdfStreamUrl = (() => {
    const base = `${buildAssetUrl("/api")}/invoices/${id}/pdf-stream`;
    const token = inv?.pdf_cache_bust_token || inv?.updated_at;
    return token ? `${base}?t=${encodeURIComponent(token)}` : base;
  })();

  const downloadPdf = () => {
    const sep = pdfStreamUrl.includes("?") ? "&" : "?";
    window.open(`${pdfStreamUrl}${sep}download=1`, "_blank");
  };

  const subtotalBefore = items.reduce((s, li) => s + (Number(li.line_total) || 0), 0);
  const discountAmount = (() => {
    const v = Number(inv.discount_value || 0);
    if (!inv.discount_type || inv.discount_type === "none" || v <= 0) return 0;
    if (inv.discount_type === "amount") return Math.min(v, subtotalBefore);
    const p = Math.max(0, Math.min(100, v));
    return subtotalBefore * (p / 100);
  })();
  const subtotal = Math.max(0, subtotalBefore - discountAmount);
  const taxAmount = subtotal * (inv.tax_rate / 100);
  const total = subtotal + taxAmount;

  const isVoided = inv.status === "voided";
  // Phase 61.1 — drafts have no I-number and need to be issued (mints I-number)
  // before they can be sent / paid / voided / edited. Distinguish TRUE drafts
  // (Phase 61.1+, no I-number) from legacy status="draft" docs that ALREADY
  // have a number (pre-Phase-61.1 they meant "not yet sent" — fully sendable).
  const isDraft = inv.status === "draft";
  const isTrueDraft = isDraft && !inv.invoice_number;
  // Phase 37 — lock = paid/settled OR voided OR backend has explicitly locked it.
  const isLocked = isVoided
    || inv.is_locked === true
    || inv.payment_status === "paid"
    || inv.payment_status === "write_off"
    || inv.payment_status === "unable_to_collect";

  const doVoid = async () => {
    setVoiding(true);
    try {
      const { data } = await api.post(`/invoices/${id}/void`, { reason: voidReason || null });
      setInv(data);
      setVoidOpen(false);
      setVoidReason("");
      toast.success("Invoice voided");
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally {
      setVoiding(false);
    }
  };

  // Phase P.6 — sync-from-booking removed (no more revisions/history UI).
  // Auto-refresh on booking edit (Phase A hook, server-side) already keeps
  // the invoice mirrored. The "Reset from booking + customer" button
  // (below) covers the manual escape hatch. `syncing` state kept only to
  // keep unrelated banner code compiling — always false in the new flow.

  // Phase 61.1 — manually promote a DRAFT to ISSUED. Mints the I-number,
  // stamps issued_at, renders the PDF.
  const doIssue = async () => {
    setIssuing(true);
    try {
      const { data } = await api.post(`/invoices/${id}/issue`);
      setInv(data);
      toast.success(`Issued · ${data.invoice_number}`);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally {
      setIssuing(false);
    }
  };

  return (
    <div className="page" data-testid="invoice-detail-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="mono text-[10px] uppercase tracking-[0.3em] text-accent">// Invoice</div>
          <h1 className="text-3xl md:text-4xl font-black tracking-tight mt-2 mono flex items-center gap-2" data-testid="invoice-number-heading">
            <span>{inv.invoice_number || "DRAFT"}</span>
            {isTrueDraft && (
              <span className="text-xs font-bold bg-amber-100 text-amber-900 border border-amber-300 px-2 py-1 rounded-md mono uppercase tracking-widest" data-testid="invoice-draft-badge">
                Draft
              </span>
            )}
          </h1>
          <div className="text-xs text-muted-foreground mt-1 flex items-center gap-3 flex-wrap">
            <span>
              {isTrueDraft
                ? `Job ${(inv.booking_date || "").slice(0, 10) || "—"}`
                : `Issued ${(inv.issued_at || "").slice(0, 10)}`}
            </span>
            <span>·</span>
            <Link to={`/bookings/${inv.booking_id}/edit`} className="text-accent hover:underline">{inv.booking_code}</Link>
            <span>·</span>
            <span className="mono uppercase tracking-widest">{inv.payment_type_name}</span>
            {inv.po_number && (
              <>
                <span>·</span>
                <span className="mono uppercase tracking-widest" data-testid="invoice-po-display">
                  PO <strong className="text-foreground">{inv.po_number}</strong>
                </span>
              </>
            )}
            {isVoided && <span className="bg-rose-100 text-rose-800 border border-rose-200 mono uppercase tracking-widest text-[10px] px-2 py-0.5 rounded">Voided</span>}
            {isLocked && !isVoided && (
              <span className="bg-slate-200 text-slate-800 border border-slate-300 mono uppercase tracking-widest text-[10px] px-2 py-0.5 rounded inline-flex items-center gap-1" data-testid="invoice-locked-pill">
                <Lock className="h-3 w-3" /> Locked (Paid)
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {isTrueDraft && (
            <Button onClick={doIssue} disabled={issuing} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="invoice-issue-button">
              <FileText className="h-4 w-4 mr-1" /> {issuing ? "Issuing…" : "Issue invoice"}
            </Button>
          )}
          {!isLocked && !isTrueDraft && (
            <Button onClick={() => setEditOpen(true)} className="btn-cta" data-testid="invoice-edit-button">
              <Edit3 className="h-4 w-4 mr-1" /> Edit invoice
            </Button>
          )}
          {isLocked && !isVoided && (
            <Button variant="outline" onClick={() => setVoidOpen(true)} data-testid="invoice-void-button" className="text-rose-700 hover:bg-rose-50 border-rose-200">
              <Ban className="h-4 w-4 mr-1" /> Void this invoice
            </Button>
          )}
          <Link to="/accounts" className="text-sm text-muted-foreground hover:text-foreground inline-flex items-center gap-1" data-testid="back-accounts">
            <ArrowLeft className="h-4 w-4" /> Back to Accounts
          </Link>
        </div>
      </div>

      {/* Phase P.11 — Send-blocked banner. Fires when the staff/manager
          "Send invoice" flow found the customer has no email. Managers
          edit the customer, then hit "Retry send" in the sidebar. */}
      {inv.invoice_send_blocked_reason === "no_customer_email" && (
        <div
          className="bg-rose-50 border border-rose-200 rounded-[6px] px-3 py-2 text-sm text-rose-900 flex items-start gap-2"
          data-testid="invoice-send-blocked-banner"
        >
          <MailX className="h-4 w-4 mt-0.5 shrink-0" />
          <div className="flex-1">
            <div className="font-bold">Invoice not sent — no customer email on file.</div>
            <div className="text-[12px] text-rose-800 mt-0.5">
              Add an email to the customer record, then use "Retry send" in the sidebar.
            </div>
          </div>
        </div>
      )}

      {/* Phase P.6 — booking-changed banner + latest-version-not-sent banner
          removed. Booking edits now auto-refresh the invoice server-side
          (Phase A hook), so the manager doesn't need to press a "Sync"
          button. If they want to nuke manual edits back to booking+customer
          truth, the "Reset from booking + customer" button in the sidebar
          covers it. */}

      {isVoided && (
        <div className="bg-rose-50 border border-rose-200 rounded-[6px] p-3 mt-4 text-sm text-rose-900 flex items-start gap-2" data-testid="invoice-voided-banner">
          <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
          <span>This invoice was voided when the booking's payment type changed to a non-invoiced type. Switch back to an invoiced type to re-activate.</span>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-6">
        <div className="lg:col-span-2 space-y-4">
          <section className="bg-card border border-border rounded-[8px] p-5" data-testid="invoice-customer">
            <h2 className="text-sm mono uppercase tracking-widest text-muted-foreground">Bill to</h2>
            <div className="font-bold text-lg mt-1">{inv.customer_snapshot?.company_name}</div>
            <div className="text-sm text-muted-foreground whitespace-pre-line">{inv.customer_snapshot?.billing_address}</div>
          </section>

          <section className="bg-card border border-border rounded-[8px] p-5" data-testid="invoice-line-items">
            <div className="flex items-center justify-between">
              <h2 className="text-sm mono uppercase tracking-widest text-muted-foreground">Line items</h2>
              <Button size="sm" variant="outline" onClick={addItem} disabled={isLocked} data-testid="invoice-add-item-button">
                <Plus className="h-3 w-3 mr-1" /> Add
              </Button>
            </div>
            <div className="mt-3 space-y-2">
              {items.length === 0 && <div className="text-xs text-muted-foreground italic">No line items.</div>}
              {items.map((li, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 items-center" data-testid={`invoice-item-${i}`}>
                  <Input className="col-span-6" placeholder="Description" value={li.description} onChange={(e) => updateItem(i, "description", e.target.value)} disabled={isLocked} data-testid={`invoice-item-desc-${i}`} />
                  <Input className="col-span-1" type="number" step="0.5" value={li.quantity} onChange={(e) => updateItem(i, "quantity", e.target.value)} disabled={isLocked} data-testid={`invoice-item-qty-${i}`} />
                  <Input className="col-span-2" type="number" step="0.01" value={li.unit_price} onChange={(e) => updateItem(i, "unit_price", e.target.value)} disabled={isLocked} data-testid={`invoice-item-price-${i}`} />
                  <div className="col-span-2 text-right mono font-bold">{formatCurrency(li.line_total, inv.currency)}</div>
                  <Button size="icon" variant="ghost" className="col-span-1" onClick={() => removeItem(i)} disabled={isLocked} data-testid={`invoice-item-remove-${i}`}>
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </div>
              ))}
            </div>
            <div className="grid grid-cols-12 gap-2 mt-4 border-t border-border pt-3 text-sm">
              <div className="col-span-9 text-right text-muted-foreground">Subtotal</div>
              <div className="col-span-3 text-right mono font-bold">{formatCurrency(subtotalBefore, inv.currency)}</div>
              {discountAmount > 0 && (
                <>
                  <div className="col-span-9 text-right text-rose-700">
                    Discount{inv.discount_type === "percent" ? ` (${inv.discount_value}%)` : ""}
                  </div>
                  <div className="col-span-3 text-right mono font-bold text-rose-700" data-testid="invoice-totals-discount">
                    −{formatCurrency(discountAmount, inv.currency)}
                  </div>
                  <div className="col-span-9 text-right text-muted-foreground">After discount</div>
                  <div className="col-span-3 text-right mono font-bold">{formatCurrency(subtotal, inv.currency)}</div>
                </>
              )}
              <div className="col-span-9 text-right text-muted-foreground">Tax ({inv.tax_rate.toFixed(2)}%)</div>
              <div className="col-span-3 text-right mono font-bold">{formatCurrency(taxAmount, inv.currency)}</div>
              <div className="col-span-9 text-right font-extrabold">Total</div>
              <div className="col-span-3 text-right mono text-lg font-extrabold">{formatCurrency(total, inv.currency)}</div>
            </div>
            <div className="mt-3">
              <Button onClick={saveItems} disabled={savingItems || isLocked} className="btn-cta" data-testid="invoice-save-items-button">
                <Save className="h-4 w-4 mr-1" /> {savingItems ? "Saving..." : "Save line items"}
              </Button>
            </div>
          </section>

          <section className="bg-card border border-border rounded-[8px] p-5" data-testid="invoice-discount-editor">
            <h2 className="text-sm mono uppercase tracking-widest text-muted-foreground">Discount</h2>
            <InvoiceDiscountEditor invoice={inv} onSaved={(d) => { setInv(d); setItems(d.line_items); }} disabled={isVoided || isLocked} />
          </section>

          <section className="bg-card border border-border rounded-[8px] p-5" data-testid="invoice-pdf-preview">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-sm mono uppercase tracking-widest text-muted-foreground">PDF preview</h2>
              <Button variant="outline" size="sm" onClick={downloadPdf} data-testid="invoice-download-button">
                <Download className="h-3 w-3 mr-1" /> Download
              </Button>
            </div>
            {inv.invoice_number ? (
              <iframe
                src={pdfStreamUrl}
                title="invoice pdf"
                className="w-full h-[600px] rounded border border-border"
                data-testid="invoice-pdf-iframe"
              />
            ) : (
              <div className="text-sm text-muted-foreground">PDF not yet generated.</div>
            )}
          </section>
        </div>

        <div className="space-y-4">
          <section className="bg-card border border-border rounded-[8px] p-5" data-testid="invoice-actions">
            <h2 className="text-sm mono uppercase tracking-widest text-muted-foreground">Actions</h2>
            <Dialog open={sendOpen} onOpenChange={(o) => {
              // Phase O — refuse to open the dialog when email sending is
              // gated. Belt-and-braces: the backend gate is authoritative,
              // but this stops a wasted round-trip + gives the user a hint.
              if (o && emailSendingGated) {
                toast.error(`Emailing invoices is a Pro feature — your ${gatedPlanCode || "current"} plan can't send. Upgrade to Pro.`);
                return;
              }
              setSendOpen(o);
              if (!o) {
                setPrefillSendId(null);
                if (resendId) {
                  const next = new URLSearchParams(searchParams);
                  next.delete("resend");
                  setSearchParams(next, { replace: true });
                }
              }
            }}>
              <DialogTrigger asChild>
                <Button
                  className="btn-cta w-full mt-3 gap-2"
                  disabled={isVoided || emailSendingGated}
                  title={emailSendingGated ? `Emailing invoices is a Pro feature — your ${gatedPlanCode || "current"} plan can't send. Upgrade to Pro.` : ""}
                  data-testid="invoice-send-button"
                >
                  <Send className="h-4 w-4" /> {emailSendingGated ? "Send via email (Pro)" : "Send via email"}
                </Button>
              </DialogTrigger>
              {/* Phase P — inline upgrade pricing hint under a gated Send button,
                  so the tooltip's plain-text limitation is compensated with a
                  visible €40+VAT anchor. */}
              {emailSendingGated && (
                <div
                  className="mt-1.5 text-[11px] text-muted-foreground flex items-center gap-1.5"
                  data-testid="invoice-send-upgrade-hint"
                >
                  <span>Unlock with Pro —</span>
                  <PriceDisplay code="pro" size="sm" />
                </div>
              )}
              <SendDialog
                invoice={inv}
                customer={customer}
                prefillSendId={prefillSendId}
                onClose={() => { setSendOpen(false); setPrefillSendId(null); }}
                onSent={(d) => { setInv(d); setSendOpen(false); setPrefillSendId(null); }}
              />
            </Dialog>
            <Button variant="outline" className="w-full mt-2" onClick={downloadPdf} data-testid="invoice-pdf-download-action">
              <Download className="h-4 w-4 mr-1" /> Download PDF
            </Button>
            {/* Phase P.11 — Retry Send when the invoice is flagged as
                blocked by "no customer email". Managers edit the customer
                record, then click this to re-fetch the live address and
                retry the send. Success clears the block flag; failure
                leaves the banner + refreshes its timestamp. */}
            {inv.invoice_send_blocked_reason === "no_customer_email" && !emailSendingGated && (
              <RetrySendButton invoiceId={inv.id} onDone={(d) => setInv(d)} />
            )}
            {/* Phase A (User Message 450) — pull current settings into
                this invoice's snapshot so the next PDF re-render reflects
                today's company branding + customer details. */}
            <RebuildFromLiveButton
              invoiceId={inv.id}
              onRebuilt={(d) => setInv(d)}
              disabled={(inv.status || "").toLowerCase() === "voided"}
            />
            {/* Phase 27 / P.10 — manager-side "copy pay-online link".
                Depends on Stripe Connect (customer pays into tenant's
                Connect account). Feature-gated with the Connect flag. */}
            {CONNECT_ENABLED && inv.payment_status !== "paid" && inv.payment_status !== "write_off" && inv.payment_status !== "unable_to_collect" && (
              <CopyPayOnlineButton invoiceId={inv.id} />
            )}
            {inv.payment_status === "paid" && inv.paid_via === "stripe_online" && (
              <div className="mt-2 text-[11px] inline-flex items-center gap-1.5 bg-sky-50 dark:bg-sky-900/30 text-sky-700 dark:text-sky-300 border border-sky-200 dark:border-sky-800 px-2 py-1 rounded" data-testid="invoice-paid-online-badge">
                Paid online via Stripe{inv.paid_at ? ` · ${inv.paid_at.slice(0,10)}` : ""}
              </div>
            )}
            {inv.payment_status === "paid" && (
              <ReceiptSendButton invoice={inv} customer={customer} onSent={(d) => setInv(d)} />
            )}
            {inv.receipt_sent_at && (
              <div className="text-[11px] text-emerald-700 mt-2" data-testid="invoice-receipt-sent-info">
                Receipt sent {(inv.receipt_sent_at || "").slice(0, 16).replace("T", " ")} → {(inv.receipt_sent_to || []).join(", ")}
              </div>
            )}
          </section>

          <section className="bg-card border border-border rounded-[8px] p-5" data-testid="invoice-template-section">
            <h2 className="text-sm mono uppercase tracking-widest text-muted-foreground">Template</h2>
            <div className="text-sm mt-2">
              Currently:&nbsp;
              <span className="font-bold" data-testid="invoice-current-template-name">
                {inv.invoice_template_snapshot?.name || "—"}
              </span>
            </div>
            <div className="mt-3 flex gap-2 items-end">
              <div className="flex-1">
                <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Switch to</Label>
                <Select value={switchingTpl} onValueChange={setSwitchingTpl}>
                  <SelectTrigger data-testid="invoice-template-select"><SelectValue placeholder="Pick a template..." /></SelectTrigger>
                  <SelectContent>
                    {allTemplates.map((t) => (
                      <SelectItem key={t.id} value={t.id} data-testid={`invoice-template-option-${t.id}`}>
                        {t.name}{t.is_active ? " · in use" : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button
                size="sm"
                disabled={isVoided || !switchingTpl || switchingTpl === inv.invoice_template_id}
                onClick={async () => {
                  try {
                    const { data } = await api.post(`/invoices/${id}/switch-template`, { template_id: switchingTpl });
                    setInv(data);
                    setSwitchingTpl("");
                    toast.success("Template switched · PDF regenerated");
                  } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
                }}
                className="btn-cta"
                data-testid="invoice-switch-template-button"
              >
                Switch
              </Button>
            </div>
            <p className="text-[11px] text-muted-foreground mt-2">
              Switching regenerates the PDF using the new template. The invoice number stays the same.
            </p>
          </section>

          <section className="bg-card border border-border rounded-[8px] p-5" data-testid="invoice-payment-status-section">
            <h2 className="text-sm mono uppercase tracking-widest text-muted-foreground">Payment status</h2>
            <Select value={inv.payment_status} onValueChange={setPaymentStatus}>
              <SelectTrigger className="mt-3" data-testid="invoice-payment-status-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {PAY_STATUS.map((s) => <SelectItem key={s.v} value={s.v}>{s.l}</SelectItem>)}
              </SelectContent>
            </Select>
            {inv.paid_at && <div className="text-[11px] text-muted-foreground mt-2">Paid at {new Date(inv.paid_at).toLocaleString()}</div>}
            {inv.payment_status === "paid" && inv.paid_method_name && (
              <div className="text-[11px] text-muted-foreground mt-1" data-testid="invoice-paid-method">
                via <strong>{inv.paid_method_name}</strong>
              </div>
            )}
            {inv.sent_at && <div className="text-[11px] text-muted-foreground mt-2">Last sent {new Date(inv.sent_at).toLocaleString()}<br />to {inv.sent_to?.join(", ")}</div>}
          </section>

          {/* Phase P.6 — audit-log panel removed. Field still persists
              server-side for support/debug (see backend/services/invoices.py)
              but is no longer exposed in InvoiceOut. */}

          {/* Phase 62.5 — per-invoice send history (Sent emails for THIS invoice). */}
          <SendHistorySection
            invoiceId={inv.id}
            onResend={(sendId) => {
              setPrefillSendId(sendId);
              setSendOpen(true);
            }}
          />

          {/* Phase P.11 — Status / payment audit timeline. Complements the
              SendHistorySection above by showing non-send transitions
              (issued, marked paid, voided, refund, retry-send, etc.). */}
          <StatusHistory entityType="invoice" entityId={inv.id} testIdPrefix="invoice-history" />

          {/* Phase P.6 — Versions/revision-history panel removed. Every
              save now overwrites in place; PDF re-renders via
              pdf_cache_bust_token. Managers who want to nuke manual
              edits back to booking + customer truth click the "Reset
              from booking + customer" button in the sidebar. */}
        </div>
      </div>

      <Dialog open={pendingPaid} onOpenChange={(o) => !o && setPendingPaid(false)}>
        <DialogContent data-testid="paid-method-pick-dialog">
          <DialogHeader>
            <DialogTitle>Mark invoice as paid</DialogTitle>
            <DialogDescription>Pick where the funds were received.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-wider font-semibold">Paid method *</Label>
            <Select
              value={paidMethodCode}
              onValueChange={setPaidMethodCode}
              onOpenChange={async (open) => {
                // Phase E — refetch when the dropdown opens so newly
                // created Paid Methods (e.g. "Cash to DIMA") appear
                // without a page reload.
                if (!open) return;
                try {
                  const { data } = await api.get("/settings/paid-methods");
                  if (Array.isArray(data)) setPaidMethods(data);
                } catch { /* silent — keep stale list */ }
              }}
            >
              <SelectTrigger data-testid="paid-method-select"><SelectValue placeholder="Choose method..." /></SelectTrigger>
              <SelectContent>
                {paidMethods.map((p) => (
                  <SelectItem key={p.id} value={p.code} data-testid={`paid-method-option-${p.code}`}>{p.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPendingPaid(false)}>Cancel</Button>
            <Button onClick={confirmPaid} disabled={!paidMethodCode} className="btn-cta" data-testid="paid-method-confirm">
              Mark paid
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Phase 37 — Edit drawer (PO + line items + discount + payment_type + customer billing + notes + payment_terms + due_date) */}
      <InvoiceEditDrawer
        open={editOpen}
        onOpenChange={setEditOpen}
        invoice={inv}
        onSaved={(d) => { setInv(d); setItems(d.line_items || []); setEditOpen(false); }}
      />

      {/* Phase 37 — Void confirmation */}
      <AlertDialog open={voidOpen} onOpenChange={setVoidOpen}>
        <AlertDialogContent data-testid="invoice-void-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Void this invoice?</AlertDialogTitle>
            <AlertDialogDescription>
              Voiding marks invoice <strong>{inv.invoice_number}</strong> as cancelled and removes it from active accounts.
              The record is preserved for audit. This action is reversible only by issuing a new invoice.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">Reason (optional)</Label>
            <Textarea
              rows={2}
              value={voidReason}
              onChange={(e) => setVoidReason(e.target.value)}
              placeholder="e.g. Customer requested invoice cancellation"
              data-testid="invoice-void-reason-input"
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={voiding} data-testid="invoice-void-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={doVoid} disabled={voiding}
              className="bg-rose-600 hover:bg-rose-700 focus:ring-rose-500"
              data-testid="invoice-void-confirm"
            >
              {voiding ? "Voiding…" : "Void invoice"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// ---------- Phase 37 — InvoiceEditDrawer ----------
function InvoiceEditDrawer({ open, onOpenChange, invoice, onSaved }) {
  const [poNumber, setPoNumber] = useState("");
  const [paymentType, setPaymentType] = useState("");
  const [items, setItems] = useState([]);
  const [discountType, setDiscountType] = useState("none");
  const [discountValue, setDiscountValue] = useState("0");
  const [billing, setBilling] = useState({ company_name: "", contact_name: "", billing_address: "", vat_number: "" });
  const [notes, setNotes] = useState("");
  const [paymentTerms, setPaymentTerms] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [reason, setReason] = useState("");
  const [refreshCompanyDetails, setRefreshCompanyDetails] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [paymentTypes, setPaymentTypes] = useState([]);
  // Phase 47 — "Use latest template" wiring. Pulls the tenant's active
  // invoice template (or the platform Standard fallback) and pastes
  // its `footer_text` into the Notes field + `payment_terms` into the
  // Payment terms field. Modal-confirmed because it overwrites whatever
  // the user has typed.
  const [templateConfirmOpen, setTemplateConfirmOpen] = useState(false);
  const [templatePulling, setTemplatePulling] = useState(false);
  const [templatePreview, setTemplatePreview] = useState(null);
  // Phase 47 — per-field 422 error surfacing.
  const [fieldErrors, setFieldErrors] = useState({});
  const fieldRefs = useRef({});

  useEffect(() => {
    if (!open || !invoice) return;
    setPoNumber(invoice.po_number || "");
    setPaymentType(invoice.payment_type_code || "");
    setItems((invoice.line_items || []).map((li) => ({ ...li })));
    setDiscountType(invoice.discount_type || "none");
    setDiscountValue(String(invoice.discount_value || "0"));
    const cs = invoice.customer_snapshot || {};
    setBilling({
      company_name: cs.company_name || "",
      contact_name: cs.contact_name || "",
      billing_address: cs.billing_address || "",
      vat_number: cs.vat_number || "",
    });
    setNotes(invoice.notes || "");
    setPaymentTerms(invoice.payment_terms || "");
    setDueDate(invoice.due_date || "");
    setReason("");
    setRefreshCompanyDetails(true);
    setFieldErrors({});
    api.get("/settings/payment-types").then(({ data }) => setPaymentTypes((data || []).filter((p) => p.generates_invoice))).catch(() => setPaymentTypes([]));
  }, [open, invoice]);

  const updateItem = (i, k, v) => {
    const next = [...items];
    next[i] = { ...next[i], [k]: v };
    if (k === "quantity" || k === "unit_price") {
      next[i].line_total = Number(((Number(next[i].quantity) || 0) * (Number(next[i].unit_price) || 0)).toFixed(2));
    }
    setItems(next);
  };
  const addItem = () => setItems([...items, { description: "", quantity: 1, unit_price: 0, line_total: 0 }]);
  const removeItem = (i) => setItems(items.filter((_, idx) => idx !== i));

  const save = async () => {
    setSubmitting(true);
    try {
      const body = {
        po_number: poNumber.trim() || null,
        line_items: items.map((li) => ({
          description: li.description || "",
          quantity: Number(li.quantity) || 0,
          unit_price: Number(li.unit_price) || 0,
        })),
        discount_type: discountType,
        discount_value: Number(discountValue) || 0,
        payment_type: paymentType || null,
        customer_billing: billing,
        notes: notes || null,
        payment_terms: paymentTerms || null,
        due_date: dueDate || null,
        reason: reason.trim() || null,
      };
      const { data } = await api.put(`/invoices/${invoice.id}`, body);
      toast.success("Invoice saved");
      setFieldErrors({});
      onSaved && onSaved(data);
    } catch (e) {
      // Phase 47 — split 422 into per-field outlines + scroll-to-first.
      const { fieldErrors: fe, generalMessage } = extractFieldErrors(e);
      setFieldErrors(fe);
      const firstField = Object.keys(fe)[0];
      if (firstField && fieldRefs.current[firstField]) {
        try { fieldRefs.current[firstField].scrollIntoView({ behavior: "smooth", block: "center" }); }
        catch { /* ignore */ }
      }
      toast.error(generalMessage || "Could not save invoice");
    } finally {
      setSubmitting(false);
    }
  };

  if (!invoice) return null;
  const cur = invoice.currency || "EUR";
  const subtotalBefore = items.reduce((s, li) => s + (Number(li.line_total) || 0), 0);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-2xl overflow-y-auto" data-testid="invoice-edit-drawer">
        <SheetHeader>
          <SheetTitle>Edit invoice {invoice.invoice_number}</SheetTitle>
          <SheetDescription>
            Saving overwrites the invoice in place and re-renders the PDF. The invoice number stays the same.
          </SheetDescription>
        </SheetHeader>

        <div className="space-y-5 mt-5">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs font-bold uppercase tracking-wider">PO number</Label>
              <Input value={poNumber} onChange={(e) => setPoNumber(e.target.value)} placeholder="e.g. PO-2026-0042" data-testid="invoice-edit-po-input" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-bold uppercase tracking-wider">Due date</Label>
              <Input type="date" value={dueDate ? dueDate.slice(0, 10) : ""} onChange={(e) => setDueDate(e.target.value)} data-testid="invoice-edit-due-date-input" />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs font-bold uppercase tracking-wider">Payment type</Label>
            <Select value={paymentType} onValueChange={setPaymentType}>
              <SelectTrigger data-testid="invoice-edit-payment-type-select"><SelectValue placeholder="Select..." /></SelectTrigger>
              <SelectContent>
                {paymentTypes.map((p) => <SelectItem key={p.code} value={p.code}>{p.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-xs font-bold uppercase tracking-wider">Line items</Label>
              <Button type="button" variant="outline" size="sm" onClick={addItem} data-testid="invoice-edit-additem">
                <Plus className="h-3 w-3 mr-1" /> Add
              </Button>
            </div>
            {items.map((li, i) => (
              <div key={i} className="grid grid-cols-12 gap-2 items-center" data-testid={`invoice-edit-item-${i}`}>
                <Input className="col-span-6" placeholder="Description" value={li.description || ""} onChange={(e) => updateItem(i, "description", e.target.value)} />
                <Input className="col-span-1" type="number" step="0.5" value={li.quantity || 0} onChange={(e) => updateItem(i, "quantity", e.target.value)} />
                <Input className="col-span-2" type="number" step="0.01" value={li.unit_price || 0} onChange={(e) => updateItem(i, "unit_price", e.target.value)} />
                <div className="col-span-2 text-right mono font-bold text-sm">{formatCurrency(li.line_total || 0, cur)}</div>
                <Button size="icon" variant="ghost" className="col-span-1" onClick={() => removeItem(i)}>
                  <Trash2 className="h-3 w-3" />
                </Button>
              </div>
            ))}
            <div className="text-right text-xs text-muted-foreground mono pt-1">
              Subtotal: {formatCurrency(subtotalBefore, cur)}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs font-bold uppercase tracking-wider">Discount type</Label>
              <Select value={discountType} onValueChange={setDiscountType}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  <SelectItem value="amount">Fixed amount</SelectItem>
                  <SelectItem value="percent">Percent</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-bold uppercase tracking-wider">Discount value</Label>
              <Input type="number" step="0.01" value={discountValue} onChange={(e) => setDiscountValue(e.target.value)} disabled={discountType === "none"} />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs font-bold uppercase tracking-wider">Customer billing</Label>
            <Input value={billing.company_name} onChange={(e) => setBilling({ ...billing, company_name: e.target.value })} placeholder="Company name" data-testid="invoice-edit-billing-company" />
            <Input value={billing.contact_name} onChange={(e) => setBilling({ ...billing, contact_name: e.target.value })} placeholder="Contact name" />
            <Textarea rows={2} value={billing.billing_address} onChange={(e) => setBilling({ ...billing, billing_address: e.target.value })} placeholder="Billing address" data-testid="invoice-edit-billing-address" />
            <Input value={billing.vat_number} onChange={(e) => setBilling({ ...billing, vat_number: e.target.value })} placeholder="VAT number" />
          </div>

          <div
            className="space-y-1.5"
            ref={(el) => { if (el) fieldRefs.current.notes = el; }}
          >
            <div className="flex items-center justify-between gap-2">
              <Label className="text-xs font-bold uppercase tracking-wider">Notes / Payment terms</Label>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={async () => {
                  setTemplatePulling(true);
                  try {
                    // Phase 47 — preview the active invoice template
                    // before confirming. We never write directly; the
                    // confirm modal does the actual paste.
                    const { data } = await api.get("/invoice-templates/active");
                    setTemplatePreview(data);
                    setTemplateConfirmOpen(true);
                  } catch (e) {
                    toast.error(formatApiError(e) || "Could not load invoice template");
                  } finally {
                    setTemplatePulling(false);
                  }
                }}
                disabled={submitting || templatePulling}
                data-testid="invoice-edit-use-latest-template"
                title="Pull the latest header/footer + payment terms from your active invoice template"
              >
                {templatePulling ? "Loading…" : "Use latest template"}
              </Button>
            </div>
            <Textarea
              rows={2}
              value={notes}
              onChange={(e) => {
                setNotes(e.target.value);
                if (fieldErrors.notes) setFieldErrors((p) => { const { notes: _, ...r } = p; return r; });
              }}
              placeholder="Internal / display notes"
              data-testid="invoice-edit-notes-input"
              className={fieldErrors.notes ? "border-red-500 ring-1 ring-red-500" : ""}
            />
            {fieldErrors.notes && (
              <div className="text-[11px] text-red-700" data-testid="invoice-edit-field-error-notes">{fieldErrors.notes}</div>
            )}
          </div>
          <div
            className="space-y-1.5"
            ref={(el) => { if (el) fieldRefs.current.payment_terms = el; }}
          >
            <Label className="text-xs font-bold uppercase tracking-wider">Payment terms</Label>
            <Textarea
              rows={2}
              value={paymentTerms}
              onChange={(e) => {
                setPaymentTerms(e.target.value);
                if (fieldErrors.payment_terms) setFieldErrors((p) => { const { payment_terms: _, ...r } = p; return r; });
              }}
              placeholder="e.g. Net 30 — pay within 30 days of issue"
              data-testid="invoice-edit-payment-terms-input"
              className={fieldErrors.payment_terms ? "border-red-500 ring-1 ring-red-500" : ""}
            />
            {fieldErrors.payment_terms && (
              <div className="text-[11px] text-red-700" data-testid="invoice-edit-field-error-payment_terms">{fieldErrors.payment_terms}</div>
            )}
          </div>

          {/* Phase 37 follow-up — refresh company bank details + payment terms */}
          <div className="bg-slate-50 border border-slate-200 rounded-md p-3 space-y-2" data-testid="invoice-edit-company-snapshot">
            <Label className="text-xs font-bold uppercase tracking-wider text-slate-700">Company details on invoice</Label>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 text-xs">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Bank name</div>
                <div className="mono text-slate-900 truncate" data-testid="invoice-edit-snapshot-bank-name">
                  {invoice.company_snapshot?.invoice_bank_name || <span className="text-slate-400 italic">— not set —</span>}
                </div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">IBAN</div>
                <div className="mono text-slate-900 truncate" data-testid="invoice-edit-snapshot-bank-iban">
                  {invoice.company_snapshot?.invoice_bank_iban || <span className="text-slate-400 italic">— not set —</span>}
                </div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">BIC</div>
                <div className="mono text-slate-900 truncate" data-testid="invoice-edit-snapshot-bank-bic">
                  {invoice.company_snapshot?.invoice_bank_bic || <span className="text-slate-400 italic">— not set —</span>}
                </div>
              </div>
            </div>
            <label className="flex items-start gap-2 cursor-pointer pt-1" data-testid="invoice-edit-refresh-company-toggle">
              <input
                type="checkbox"
                checked={refreshCompanyDetails}
                onChange={(e) => setRefreshCompanyDetails(e.target.checked)}
                className="mt-0.5 h-4 w-4 rounded border-slate-400 text-accent focus:ring-accent"
                data-testid="invoice-edit-refresh-company-checkbox"
              />
              <span className="text-xs leading-snug">
                <strong>Refresh bank details + payment terms from current Settings.</strong>
                <span className="block text-slate-600 mt-0.5">
                  Tick to pull the latest bank details and payment terms from your Invoice Settings.
                  Company name, address, and VAT stay locked to the original issue date.
                </span>
              </span>
            </label>
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs font-bold uppercase tracking-wider">Reason for change <span className="text-muted-foreground font-normal lowercase">(stamped on the audit trail, optional)</span></Label>
            <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Customer requested PO + later due date" data-testid="invoice-edit-reason-input" />
          </div>
        </div>

        <SheetFooter className="mt-6 gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting} data-testid="invoice-edit-cancel">Cancel</Button>
          <Button onClick={save} disabled={submitting} className="btn-cta" data-testid="invoice-edit-save">
            <Save className="h-4 w-4 mr-1" /> {submitting ? "Saving…" : "Save changes"}
          </Button>
        </SheetFooter>

        {/* Phase 47 — confirmation before overwriting the user's typed
            notes + payment terms with the active template. */}
        <AlertDialog open={templateConfirmOpen} onOpenChange={setTemplateConfirmOpen}>
          <AlertDialogContent data-testid="invoice-edit-template-confirm-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle>Use latest template?</AlertDialogTitle>
              <AlertDialogDescription>
                This will replace your current <strong>Notes</strong> and <strong>Payment terms</strong> with the active invoice template's footer + payment-terms text. Anything you've typed in those fields will be lost.
              </AlertDialogDescription>
            </AlertDialogHeader>
            {templatePreview && (
              <div className="space-y-2 my-2">
                <div className="text-[11px] mono uppercase tracking-widest text-muted-foreground">
                  From template: <span className="font-bold">{templatePreview.name || "Standard"}</span>
                </div>
                {templatePreview.footer_text && (
                  <div className="bg-secondary/40 border border-border rounded-md p-2 text-[11px]">
                    <div className="mono uppercase tracking-wider text-muted-foreground mb-1">New notes</div>
                    <pre className="font-mono whitespace-pre-wrap break-words text-[11px]">{templatePreview.footer_text}</pre>
                  </div>
                )}
                {templatePreview.payment_terms && (
                  <div className="bg-secondary/40 border border-border rounded-md p-2 text-[11px]">
                    <div className="mono uppercase tracking-wider text-muted-foreground mb-1">New payment terms</div>
                    <pre className="font-mono whitespace-pre-wrap break-words text-[11px]">{templatePreview.payment_terms}</pre>
                  </div>
                )}
                {!templatePreview.footer_text && !templatePreview.payment_terms && (
                  <div className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-md p-2">
                    The active template has no footer or payment-terms text — applying it will clear those fields on this invoice.
                  </div>
                )}
              </div>
            )}
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="invoice-edit-template-confirm-cancel">Cancel</AlertDialogCancel>
              <AlertDialogAction
                data-testid="invoice-edit-template-confirm-accept"
                onClick={() => {
                  if (templatePreview) {
                    setNotes(templatePreview.footer_text || "");
                    setPaymentTerms(templatePreview.payment_terms || "");
                    toast.success("Pasted latest template — review then Save");
                  }
                  setTemplateConfirmOpen(false);
                  setTemplatePreview(null);
                }}
              >
                Replace fields
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </SheetContent>
    </Sheet>
  );
}

function InvoiceDiscountEditor({ invoice, onSaved, disabled }) {
  const [discountType, setDiscountType] = useState(invoice.discount_type || "none");
  const [discountValue, setDiscountValue] = useState(String(invoice.discount_value || "0"));
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    setDiscountType(invoice.discount_type || "none");
    setDiscountValue(String(invoice.discount_value || "0"));
  }, [invoice.id, invoice.discount_type, invoice.discount_value]);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch(`/invoices/${invoice.id}/discount`, {
        discount_type: discountType,
        discount_value: Number(discountValue) || 0,
      });
      toast.success("Discount updated");
      onSaved?.(data);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally { setSaving(false); }
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-3 items-end">
      <div className="space-y-1">
        <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Type</Label>
        <Select value={discountType} onValueChange={setDiscountType} disabled={disabled}>
          <SelectTrigger data-testid="invoice-discount-type-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="none">No discount</SelectItem>
            <SelectItem value="amount">Amount off</SelectItem>
            <SelectItem value="percent">Percent off</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-1">
        <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">
          {discountType === "percent" ? "Percent (%)" : "Amount"}
        </Label>
        <Input
          type="number"
          min="0"
          step={discountType === "percent" ? "0.1" : "0.01"}
          value={discountValue}
          onChange={(e) => setDiscountValue(e.target.value)}
          disabled={disabled || discountType === "none"}
          data-testid="invoice-discount-value-input"
        />
      </div>
      <Button onClick={save} disabled={saving || disabled} className="btn-cta" data-testid="invoice-save-discount-button">
        {saving ? "Saving..." : "Save discount"}
      </Button>
    </div>
  );
}

function ReceiptSendButton({ invoice, customer, onSent }) {
  const [open, setOpen] = useState(false);
  const candidateEmails = (customer?.emails || []).filter((e) => e.email);
  const initialChecks = {};
  candidateEmails.forEach((e) => { initialChecks[e.email] = !!e.receives_invoices; });
  const [checks, setChecks] = useState(initialChecks);
  const [extra, setExtra] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    const ch = {};
    (customer?.emails || []).forEach((e) => { if (e.email) ch[e.email] = !!e.receives_invoices; });
    setChecks(ch);
  }, [customer?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const send = async () => {
    const recipients = [
      ...Object.keys(checks).filter((k) => checks[k]),
      ...extra.split(",").map((s) => s.trim()).filter((s) => s.includes("@")),
    ];
    if (!recipients.length) return toast.error("Pick at least one recipient");
    setBusy(true);
    try {
      const { data } = await api.post(`/invoices/${invoice.id}/send-receipt`, { recipient_emails: recipients });
      toast.success("Payment receipt sent");
      onSent?.(data);
      setOpen(false);
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" className="w-full mt-2" data-testid="invoice-send-receipt">
          <Send className="h-4 w-4 mr-1" /> Send payment receipt
        </Button>
      </DialogTrigger>
      <DialogContent data-testid="invoice-receipt-modal">
        <DialogHeader>
          <DialogTitle>Send payment receipt</DialogTitle>
          <DialogDescription>Confirms payment for {invoice.invoice_number}.</DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          {candidateEmails.length > 0 ? candidateEmails.map((e) => (
            <label key={e.email} className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={!!checks[e.email]} onChange={(ev) => setChecks((c) => ({ ...c, [e.email]: ev.target.checked }))} data-testid={`invoice-receipt-email-${e.email}`} />
              <span>{e.email}{e.receives_invoices ? <span className="ml-1 text-[10px] mono uppercase text-accent">primary</span> : null}</span>
            </label>
          )) : <div className="text-sm text-muted-foreground">Customer has no emails on file.</div>}
          <div className="pt-2">
            <Label className="text-xs">Other (comma-separated)</Label>
            <Input value={extra} onChange={(ev) => setExtra(ev.target.value)} placeholder="extra@example.com" data-testid="invoice-receipt-extra-input" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={send} disabled={busy} className="btn-cta" data-testid="invoice-receipt-send-confirm">{busy ? "Sending..." : "Send receipt"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SendDialog({ invoice, customer, onClose, onSent, prefillSendId }) {
  const candidates = (customer?.emails || []).map((e) => e.email);
  const preferred = (customer?.emails || []).filter((e) => e.receives_invoices).map((e) => e.email);
  const [selected, setSelected] = useState(new Set(preferred.length ? preferred : candidates));
  const [includeAccounts, setIncludeAccounts] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  // Phase 62.4 — per-send overrides + extra notes.
  const [defaults, setDefaults] = useState({ subject: "", greeting: "", body: "", signature: "" });
  const [settings, setSettings] = useState({
    payment_instructions: "",
    invoice_bank_name: "",
    invoice_bank_iban: "",
    invoice_bank_bic: "",
    company: {},
  });
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [extraNotes, setExtraNotes] = useState("");
  const [loadingDefaults, setLoadingDefaults] = useState(true);
  const [prefillMeta, setPrefillMeta] = useState(null);
  const bodyRef = useRef(null);

  // Tiny client-side `{{token}}` substitution mirroring the backend renderer
  // so the dialog shows the resolved subject/body instead of raw tokens.
  const ctx = (() => {
    const cust = invoice.customer_snapshot || {};
    const co = (settings.company || {});
    const total = (invoice.total_amount ?? invoice.total ?? 0);
    const currencySym = (invoice.currency_symbol) || (invoice.currency === "EUR" ? "€" : "");
    return {
      invoice_number: invoice.invoice_number || "",
      total: `${currencySym}${Number(total || 0).toFixed(2)}`,
      due_date: (invoice.due_date || "").slice(0, 10),
      issued_date: (invoice.issued_at || "").slice(0, 10),
      customer_name: cust.company_name || cust.contact_name || "",
      customer_company: cust.company_name || "",
      customer_contact: cust.contact_name || "",
      company_name: co.name || "",
      company_phone: co.phone || "",
      company_email: co.email || "",
      payment_type_name: invoice.payment_type_name || invoice.payment_type || "",
    };
  })();
  const renderTokens = (text) =>
    String(text || "").replace(/\{\{\s*([a-zA-Z0-9_]+)\s*\}\}/g, (_, k) =>
      ctx[k] === undefined || ctx[k] === null ? "" : String(ctx[k])
    );

  // Load defaults + settings + (optional) prior-send pre-fill on open.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [{ data: tpl }, { data: setg }] = await Promise.all([
          api.get("/invoice-templates/email"),
          api.get("/settings"),
        ]);
        if (cancelled) return;
        setDefaults(tpl || {});
        setSettings(setg || {});
        // If re-send pre-fill is requested, fetch the original send row and
        // hydrate subject/body verbatim — easier than re-rendering tokens.
        let priorSend = null;
        if (prefillSendId) {
          try {
            const { data: history } = await api.get(`/invoices/${invoice.id}/sends`);
            priorSend = (history || []).find((s) => s.id === prefillSendId) || null;
          } catch (_e) { /* ignore */ }
        }
        if (priorSend) {
          setSubject(priorSend.subject || "");
          // The stored body is rendered HTML; we drop into the text editor so
          // the manager can tweak. Stripping tags keeps the textarea sensible.
          const txt = (priorSend.body || "")
            .replace(/<style[\s\S]*?<\/style>/gi, "")
            .replace(/<[^>]+>/g, "")
            .replace(/&nbsp;/g, " ")
            .replace(/&amp;/g, "&")
            .replace(/&lt;/g, "<")
            .replace(/&gt;/g, ">")
            .replace(/\n{3,}/g, "\n\n")
            .trim();
          setBody(txt || "");
          if (priorSend.recipients?.length) {
            setSelected(new Set(priorSend.recipients));
          }
          setIncludeAccounts(!!priorSend.included_accounts_email);
          setPrefillMeta({
            sent_at: priorSend.sent_at,
          });
        } else {
          // Initial defaults: use the tenant template, with tokens resolved.
          setSubject("");
          setBody("");
        }
      } finally {
        if (!cancelled) setLoadingDefaults(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoice.id, prefillSendId]);

  // The PREVIEW of what will land in the email body — used when subject/body
  // overrides are blank. Mirrors the backend's stitch order.
  const renderedSubjectPreview = renderTokens(subject || defaults.subject || "");
  const renderedBodyPreview = (() => {
    if (body) return body; // override wins
    const parts = [
      renderTokens(defaults.greeting || ""),
      renderTokens(defaults.body || ""),
    ];
    if (extraNotes.trim()) parts.push(extraNotes.trim());
    if (defaults.signature) parts.push(renderTokens(defaults.signature));
    return parts.filter((p) => (p || "").trim().length > 0).join("\n\n");
  })();

  const toggle = (email) => {
    const next = new Set(selected);
    if (next.has(email)) next.delete(email); else next.add(email);
    setSelected(next);
  };

  const insertAtCursor = (text) => {
    const el = bodyRef.current;
    if (!el) { setBody((b) => (b || renderedBodyPreview) + (b ? "\n\n" : "\n\n") + text); return; }
    // First override-edit copies the rendered preview into the textarea so the
    // manager isn't editing an empty field with hidden defaults.
    const base = body || renderedBodyPreview;
    const start = el.selectionStart ?? base.length;
    const end = el.selectionEnd ?? start;
    const sep = base && !base.endsWith("\n") ? "\n\n" : "";
    const next = base.slice(0, start) + sep + text + base.slice(end);
    setBody(next);
    requestAnimationFrame(() => {
      try {
        el.focus();
        const caret = start + sep.length + text.length;
        el.setSelectionRange(caret, caret);
      } catch (_e) { /* ignore */ }
    });
  };

  const quickInsertPaymentInstructions = () => {
    const t = renderTokens(settings.payment_instructions || "");
    if (!t) { toast.info("No payment instructions saved in Settings."); return; }
    insertAtCursor(t);
  };
  const quickInsertBankDetails = () => {
    const lines = [];
    if (settings.invoice_bank_name) lines.push(`Bank: ${settings.invoice_bank_name}`);
    if (settings.invoice_bank_iban) lines.push(`IBAN: ${settings.invoice_bank_iban}`);
    if (settings.invoice_bank_bic) lines.push(`BIC: ${settings.invoice_bank_bic}`);
    if (!lines.length) { toast.info("No bank details saved in Settings."); return; }
    insertAtCursor(lines.join("\n"));
  };
  const quickInsertVatNumber = () => {
    const vat = settings.company?.vat_number;
    if (!vat) { toast.info("No VAT number saved in Settings."); return; }
    insertAtCursor(`Our VAT number: ${vat}`);
  };
  const quickInsertCompanyContact = () => {
    const co = settings.company || {};
    const lines = [];
    if (co.name) lines.push(co.name);
    if (co.phone) lines.push(`Phone: ${co.phone}`);
    if (co.email) lines.push(`Email: ${co.email}`);
    if (!lines.length) { toast.info("No company contact details saved in Settings."); return; }
    insertAtCursor(lines.join("\n"));
  };

  const send = async () => {
    setSubmitting(true);
    try {
      const payload = {
        recipient_emails: Array.from(selected),
        include_accounts_email: includeAccounts,
      };
      if (subject.trim()) payload.email_subject_override = subject.trim();
      if (body.trim()) payload.email_body_override = body.trim();
      if (extraNotes.trim() && !body.trim()) payload.extra_notes = extraNotes.trim();
      const { data } = await api.post(`/invoices/${invoice.id}/send`, payload);
      toast.success("Invoice sent");
      onSent?.(data);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally { setSubmitting(false); }
  };

  // Phase P.6 — revision-mismatch banner removed. There are no revisions
  // to mismatch; every save overwrites in place and the PDF always
  // reflects the CURRENT invoice.
  const revisionMismatch = false;

  return (
    <DialogContent
      className="max-w-3xl max-h-[90vh] overflow-y-auto"
      data-testid="invoice-send-dialog"
    >
      <DialogHeader>
        <DialogTitle>Send invoice {invoice.invoice_number || "(draft)"}</DialogTitle>
        <DialogDescription>
          {prefillSendId
            ? "Re-send — body pre-filled from the previous send. Edit before dispatching."
            : "Subject and body fall back to your Email defaults if left blank."}
        </DialogDescription>
      </DialogHeader>

      {/* Phase P.6 — revision-mismatch warning removed (no revisions). */}
      {revisionMismatch && null}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-1">
        {/* Left: recipients + extras */}
        <div className="space-y-3">
          <div className="space-y-1">
            <div className="mono text-[10px] uppercase tracking-widest text-muted-foreground">Customer emails</div>
            {candidates.length === 0 ? (
              <div className="text-sm text-muted-foreground">No emails on file.</div>
            ) : candidates.map((em) => (
              <label key={em} className="flex items-center gap-2 text-sm">
                <Checkbox checked={selected.has(em)} onCheckedChange={() => toggle(em)} data-testid={`send-recipient-${em}`} />
                {em}
                {(customer?.emails || []).find((e) => e.email === em && e.receives_invoices) && (
                  <span className="text-[10px] mono uppercase tracking-widest text-accent">Receives invoices</span>
                )}
              </label>
            ))}
            <div className="border-t border-border pt-2 mt-2">
              <label className="flex items-center gap-2 text-sm">
                <Checkbox checked={includeAccounts} onCheckedChange={(v) => setIncludeAccounts(!!v)} data-testid="send-include-accounts" />
                Also send to accounts dept (from Settings)
              </label>
            </div>
          </div>

          <div className="space-y-1">
            <label className="text-[11px] mono uppercase tracking-widest text-muted-foreground">
              Quick insert
            </label>
            <div className="flex flex-wrap gap-1.5">
              <Button type="button" variant="outline" size="sm" className="h-7 text-[11px]"
                onClick={quickInsertPaymentInstructions} data-testid="send-quick-insert-payment">
                Payment instructions
              </Button>
              <Button type="button" variant="outline" size="sm" className="h-7 text-[11px]"
                onClick={quickInsertBankDetails} data-testid="send-quick-insert-bank">
                Bank details
              </Button>
              <Button type="button" variant="outline" size="sm" className="h-7 text-[11px]"
                onClick={quickInsertVatNumber} data-testid="send-quick-insert-vat">
                VAT number
              </Button>
              <Button type="button" variant="outline" size="sm" className="h-7 text-[11px]"
                onClick={quickInsertCompanyContact} data-testid="send-quick-insert-contact">
                Company contact
              </Button>
            </div>
            <p className="text-[10px] text-muted-foreground">
              Inserts into the Body field below at the cursor.
            </p>
          </div>

          <div className="space-y-1">
            <label className="text-[11px] mono uppercase tracking-widest text-muted-foreground">
              Extra notes (added between body and signature)
            </label>
            <Textarea
              value={extraNotes}
              onChange={(e) => setExtraNotes(e.target.value)}
              rows={3}
              placeholder="One-off note for this send (optional). Ignored when Body override is in use."
              data-testid="send-extra-notes"
            />
          </div>
        </div>

        {/* Right: subject + body override */}
        <div className="space-y-3">
          <div className="space-y-1">
            <label className="text-[11px] mono uppercase tracking-widest text-muted-foreground">
              Subject (blank = use defaults)
            </label>
            <Input
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder={renderedSubjectPreview || (defaults.subject || "")}
              maxLength={240}
              data-testid="send-subject-override"
            />
            {!subject && (
              <p className="text-[10px] text-muted-foreground" data-testid="send-subject-preview">
                Will send as: <span className="mono">{renderedSubjectPreview || "—"}</span>
              </p>
            )}
          </div>

          <div className="space-y-1">
            <label className="text-[11px] mono uppercase tracking-widest text-muted-foreground">
              Body override (blank = use defaults)
            </label>
            <Textarea
              ref={bodyRef}
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={10}
              placeholder={renderedBodyPreview || "Composed from your Email defaults."}
              className="font-mono text-xs"
              data-testid="send-body-override"
            />
            {!body && (
              <details className="text-[11px] text-muted-foreground" data-testid="send-body-preview-details">
                <summary className="cursor-pointer">Preview composed body</summary>
                <pre className="bg-secondary/30 border border-border rounded p-2 mt-1 whitespace-pre-wrap font-sans">
                  {renderedBodyPreview || "—"}
                </pre>
              </details>
            )}
          </div>
        </div>
      </div>

      <DialogFooter className="mt-3">
        <Button variant="outline" onClick={onClose} disabled={submitting}>Cancel</Button>
        <Button onClick={send} disabled={submitting || loadingDefaults || selected.size === 0} className="btn-cta" data-testid="send-confirm-button">
          {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Send className="h-4 w-4 mr-1" /> Send</>}
        </Button>
      </DialogFooter>
    </DialogContent>
  );
}

// Phase 27 — Copy public pay-online link to clipboard so the manager can
// share it via SMS/WhatsApp directly with the customer.
function CopyPayOnlineButton({ invoiceId }) {
  const [busy, setBusy] = useState(false);
  const copy = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/invoices/${invoiceId}/pay-link`, {});
      const link = data.share_link || data.checkout_url;
      try {
        await navigator.clipboard.writeText(link);
        toast.success("Pay-online link copied — paste into SMS / WhatsApp / email");
      } catch {
        // Fallback for non-clipboard environments — show a tiny prompt.
        window.prompt("Copy this link:", link);
      }
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Button
      variant="outline"
      className="w-full mt-2"
      onClick={copy}
      disabled={busy}
      data-testid="invoice-copy-pay-link"
    >
      {busy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Link2 className="h-4 w-4 mr-1" />}
      {busy ? "Generating…" : "Copy pay-online link"}
    </Button>
  );
}

// Phase 62.5 — Per-invoice send history. Hangs off /api/invoices/{id}/sends.
// Pre-Phase-62 rows are flagged `is_historical=true` and lack subject/body —
// we show a soft banner above them so the manager understands the gaps.
function SendHistorySection({ invoiceId, onResend }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});

  const load = async () => {
    try {
      const { data } = await api.get(`/invoices/${invoiceId}/sends`);
      setRows(Array.isArray(data) ? data : []);
    } catch (_e) {
      // Quiet — most invoices won't have history yet.
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [invoiceId]); // eslint-disable-line react-hooks/exhaustive-deps

  const hasHistorical = rows.some((r) => r.is_historical);

  return (
    <section className="bg-card border border-border rounded-[8px] p-5" data-testid="invoice-send-history">
      <div className="flex items-center justify-between gap-2">
        <h2 className="text-sm mono uppercase tracking-widest text-muted-foreground flex items-center gap-1">
          <Mail className="h-3 w-3" /> Send history
        </h2>
        <span className="text-[10px] mono uppercase tracking-widest text-muted-foreground" data-testid="send-history-count">
          {rows.length} {rows.length === 1 ? "send" : "sends"}
        </span>
      </div>

      {loading && (
        <div className="text-xs text-muted-foreground italic mt-3">Loading…</div>
      )}
      {!loading && rows.length === 0 && (
        <div className="text-xs text-muted-foreground italic mt-3" data-testid="send-history-empty">
          This invoice hasn&apos;t been emailed yet.
        </div>
      )}
      {!loading && hasHistorical && (
        <div className="mt-3 text-[11px] bg-amber-50 border border-amber-200 text-amber-900 rounded-[4px] p-2" data-testid="send-history-historical-banner">
          Sends before Phase 62 are listed for the date + recipients only — subject and body weren&apos;t captured.
        </div>
      )}

      <div className="mt-3 space-y-2">
        {rows.map((r) => {
          const open = !!expanded[r.id];
          return (
            <div key={r.id} className="border-l-2 border-border pl-3 pb-2" data-testid={`send-history-row-${r.id}`}>
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <div className="text-xs">
                  <div className="font-bold">
                    {(r.sent_at || "").slice(0, 16).replace("T", " ")}
                    {r.is_historical && (
                      <span className="ml-2 text-[9px] uppercase tracking-widest bg-amber-100 text-amber-800 border border-amber-300 px-1.5 py-0.5 rounded">
                        Historical
                      </span>
                    )}
                    {r.custom_body_used && !r.is_historical && (
                      <span className="ml-2 text-[9px] uppercase tracking-widest bg-sky-100 text-sky-800 border border-sky-200 px-1.5 py-0.5 rounded">
                        Custom body
                      </span>
                    )}
                  </div>
                  <div className="text-muted-foreground">
                    by {r.sent_by_user_email || "—"}
                  </div>
                  <div className="text-muted-foreground mt-0.5">
                    To: {(r.recipients || []).join(", ") || "—"}
                    {r.included_accounts_email && (
                      <span className="ml-1 text-[10px] mono uppercase text-accent">+ accounts</span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  {!r.is_historical && (
                    <Button
                      size="sm" variant="outline" className="h-7 text-[11px]"
                      onClick={() => setExpanded((e) => ({ ...e, [r.id]: !open }))}
                      data-testid={`send-history-toggle-${r.id}`}
                    >
                      {open ? "Hide" : "View"}
                    </Button>
                  )}
                  <Button
                    size="sm" variant="outline" className="h-7 text-[11px]"
                    onClick={() => onResend?.(r.id)}
                    data-testid={`send-history-resend-${r.id}`}
                  >
                    <RotateCw className="h-3 w-3 mr-1" /> Re-send
                  </Button>
                </div>
              </div>
              {open && !r.is_historical && (
                <div className="mt-2 space-y-1 text-[11px]" data-testid={`send-history-detail-${r.id}`}>
                  <div className="mono uppercase tracking-widest text-muted-foreground">Subject</div>
                  <div className="font-semibold">{r.subject || "—"}</div>
                  {r.body && (
                    <>
                      <div className="mono uppercase tracking-widest text-muted-foreground mt-2">Body</div>
                      <div
                        className="prose prose-sm max-w-none bg-secondary/30 border border-border rounded p-2 text-[11px]"
                        dangerouslySetInnerHTML={{ __html: r.body }}
                      />
                    </>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}


// Phase P.6 — "Reset from booking + customer" button.
// Discards manual edits and pulls current customer profile + booking
// (line items, discount, address) + settings (company branding, bank,
// payment terms) back onto the invoice. This is the single, clear
// escape hatch — no revision history, no "sync" button, no version
// mismatches. If the manager wants live data, one click.
function RebuildFromLiveButton({ invoiceId, onRebuilt, disabled }) {
  const [busy, setBusy] = useState(false);
  const click = async () => {
    if (disabled || busy) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/invoices/${invoiceId}/rebuild-from-live`);
      onRebuilt?.(data);
      toast.success("Reset from booking + customer");
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Button
      variant="ghost"
      className="w-full mt-2 text-[12px]"
      onClick={click}
      disabled={disabled || busy}
      data-testid="invoice-rebuild-from-live"
      title="Discard manual edits and pull current customer + booking + settings into this invoice"
    >
      {busy ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <RefreshCcw className="h-3.5 w-3.5 mr-1" />}
      Reset from booking + customer
    </Button>
  );
}


// Phase P.11 — Retry Send. Only rendered when the invoice carries the
// `no_customer_email` block flag. Calls the dedicated retry endpoint so
// the audit trail distinguishes "first-time send" from "operator retry".
function RetrySendButton({ invoiceId, onDone }) {
  const [busy, setBusy] = useState(false);
  const click = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/invoices/${invoiceId}/retry-send`);
      onDone?.(data);
      toast.success("Invoice sent");
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.code === "no_customer_email") {
        toast.error(detail.message || "Customer still has no email on file");
      } else {
        toast.error(formatApiError(detail));
      }
    } finally {
      setBusy(false);
    }
  };
  return (
    <Button
      variant="outline"
      className="w-full mt-2 border-rose-300 text-rose-800 hover:bg-rose-50"
      onClick={click}
      disabled={busy}
      data-testid="invoice-retry-send-button"
      title="Send this invoice again — pulls the customer's latest email address"
    >
      {busy ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <RotateCw className="h-3.5 w-3.5 mr-1" />}
      Retry send
    </Button>
  );
}

